'use client'

import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { useProductCardHover } from '@/feature/product/hooks/useProductCardHover'
import {
  PRODUCT_IMAGE_SIZE_MEDIUM,
  PRODUCT_IMAGE_SIZE_THUMBNAIL,
} from '@/feature/product/services/product.constants'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { Card } from '@/ui/cards/card/Card'
import { Icon } from '@/ui/icons/icon/Icon'
import { Link } from '@/ui/links/link/Link.client'
import { CloudflarePictureProduct } from '@/ui/media/cloudflare-picture-product/CloudflarePictureProduct.client'
import { TextSkeleton } from '@/ui/skeleton/TextSkeleton'
import { gql } from '@gql-generated/gql'
import type {
  ProductCard_ProductPriceAndStockFragment,
  ProductCardFragment,
} from '@gql-generated/graphql'
import { ProductType, Tag as TagType } from '@gql-generated/graphql'
import type React from 'react'
import { forwardRef, Suspense, useMemo } from 'react'
import { useProductCardAnalytics } from '../../hooks/useProductAnalytics'
import { getSafeProductName } from '../../services/product.filtering'
import { createDisplayProductScreenIntent } from '../../services/product.intents'
import { toProductPage } from '../../services/product.routing'
import { getAvailabilityIssue, hasDisplayablePrice } from '../../utils/productBuyBox'
import { getProductDisplayPrice } from '../../utils/productDisplayPrice'
import { AddProductToFavoriteButton } from '../add-product-to-favorite-button/AddProductToFavoriteButton.client'
import { ProductAvailabilityIssueCard } from '../product-availability-issue-card/ProductAvailabilityIssueCard.client'
import { ProductPriceLoginLink } from '../product-price-login-link/ProductPriceLoginLink'
import { ProductPrice } from '../product-price/ProductPrice.client'
import { ProductTag } from '../product-tag/ProductTag.client'
import { ProductCardAddToCartButton } from './ProductCardAddToCartButton.client'
import { ProductCardBadges } from './ProductCardBadges.client'
import { ProductCardHoverGroup } from './ProductCardHoverGroup.client'

/** A list of tags that are not displayed on the product card. */
const tagsExcluded = [TagType.CustomPrice]

export type ProductCardProps = {
  /** The product displayed */
  product: ProductCardFragment

  productPriceAndStock?: {
    priceAndStockData?: ProductCard_ProductPriceAndStockFragment | null
    priceAndStockIsLoading?: boolean
  }

  /** The content after all the content of the card */
  bottomNode?: React.ReactNode

  /** Is the component responsive */
  responsive?: boolean

  /** Is the tag hidden */
  hideTag?: boolean

  /** Additional classes to add to the component */
  className?: string

  /** Indicates if the product is a favorite */
  isFavorite?: boolean

  /** Indicates if the AddProductToFavoriteButton is displayed */
  isAddableToFavorites?: boolean

  /** Restricts the component's size if true */
  restreintWidth?: boolean

  customTag?: React.ReactNode

  badgesWrapperClassname?: string

  /** The position of the product in the list */
  position?: number

  /** The source of the product */
  source?: string | 'product-bundle'

  /** The phone number of the user's favorite point of sale */
  favoritePOSPhoneNumber?: string

  // Opts this card out of the hover behavior (stroke + quick add-to-cart CTA), for example on the equivalent slider
  noQuickAddToCart?: boolean

  // When true, an ancestor already renders a ProductCardHoverGroup (bundle case), this card skips its own
  hasExternalHover?: boolean
}

gql(/* GraphQL */ `
  fragment BundleProductCardInfo on Product {
    reference
    name
    type
    brand {
      name
      urlKey
    }
    mainImage {
      ...FullPicture
    }
    stock {
      packaging {
        ...FullPackaging
      }
    }
  }
`)

gql(/* GraphQL */ `
  fragment ProductCardBase on Product {
    name
    urlKey
    reference
    brand {
      logo {
        ...FullPicture
      }
      name
      urlKey
    }
    mainImage {
      ...FullPicture
    }
    type
    nbArticles
  }
`)

gql(/* GraphQL */ `
  fragment ProductCard_AddToCart on Product {
    reference
    canAddToCart
    price {
      exclTax
    }
    stock {
      packaging {
        minimalSalesQuantity
        incrementQuantity
        quantity
      }
    }
  }
`)

gql(/* GraphQL */ `
  fragment ProductCard_ProductPriceAndStock on Product {
    ...ProductCard_AddToCart
    ...ProductCalculatedPrice
    parentReference
    urlKey
    bestArticlePrice
    price {
      ...FullPrice
    }
    clickAndCollectStatus
    stock {
      ...ProductStock_Availabilities
      isPriceAvailable
      isSellable
      packaging {
        ...FullPackaging
      }
    }
    isEquivalentProductsLinkVisible
    analytics {
      promotionStatus
      profileCase
    }
  }
`)

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ProductCard = forwardRef(function ProductCard(
  {
    bottomNode,
    className,
    customTag,
    hideTag = false,
    product,
    productPriceAndStock,
    responsive,
    restreintWidth,
    badgesWrapperClassname,
    position,
    source,
    isFavorite,
    isAddableToFavorites,
    favoritePOSPhoneNumber,
    noQuickAddToCart,
    hasExternalHover,
  }: ProductCardProps,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.ReactElement {
  const { t } = useTranslation(['productCard'])

  const isAuthenticated = useIsAuthenticated()

  const { config } = useSiteConfig()
  const isClientHydrated = useNextIsClientHydrated()
  // useSiteConfig() is skipped during SSR, so we stay false until hydrated to avoid a mismatch if the client's Apollo cache is already warm
  const isPublicPriceEnabled = isClientHydrated
    ? (config?.catalog?.isPublicPricesEnabled ?? false)
    : false
  const isClickAndCollectEnabled = config?.sales?.isClickAndCollectEnabled ?? false

  const isProduct = product.type == ProductType.Product
  const showPricesAndEcoPart = isPublicPriceEnabled || isAuthenticated

  const mergedProduct = useMemo(
    () =>
      productPriceAndStock?.priceAndStockData
        ? { ...product, ...productPriceAndStock.priceAndStockData }
        : product,
    [product, productPriceAndStock],
  )

  const productName = getSafeProductName(mergedProduct.name)

  const { isQuickAddToCartActivated, managesOwnHover, showAddToCart } = useProductCardHover({
    isArticle: mergedProduct.type === ProductType.Article,
    canAddToCart: !!mergedProduct.canAddToCart,
    noQuickAddToCart,
    hasExternalHover,
  })

  const { trackProductPageAnalytics } = useProductCardAnalytics({
    product: mergedProduct,
    position,
    isAuthenticated,
    source,
  })

  const productRoutingIntent = useMemo(
    () =>
      mergedProduct.urlKey ? createDisplayProductScreenIntent(mergedProduct.urlKey) : undefined,
    [mergedProduct.urlKey],
  )

  const tag = mergedProduct.price?.tag
  const showDefaultTag = !!tag && !hideTag && !tagsExcluded.includes(tag)
  const showTag = !!customTag || showDefaultTag

  const availabilityIssue = useMemo(
    () =>
      getAvailabilityIssue({
        product: mergedProduct,
        isPublicPriceEnabled,
        isProduct,
        isAuthenticated,
      }),
    [mergedProduct, isPublicPriceEnabled, isProduct, isAuthenticated],
  )

  const hasPrice = hasDisplayablePrice({ product: mergedProduct, isProduct, availabilityIssue })

  const displayPrice = useMemo(() => getProductDisplayPrice(mergedProduct), [mergedProduct])

  const priceSkeleton = (
    <div className="flex flex-col gap-1">
      <TextSkeleton className="w-13" size="xl" leading="normal" />
      <TextSkeleton className="w-17" size="lg" leading="normal" />
    </div>
  )

  const subArticlesSkeleton = <TextSkeleton className="w-16" size="md" leading="light" />

  return (
    <div data-testid="product-card" data-product-id={mergedProduct.reference} ref={ref}>
      <ProductCardHoverGroup disabled={!managesOwnHover}>
        <Card
          radius="sm"
          className={twMerge(
            className,
            'flex size-full flex-col text-brand-2-900',
            bottomNode ? 'gap-y-2' : '',
            responsive ? 'desktop:max-w-[20rem]' : 'max-w-[20rem]',
            restreintWidth ? 'min-w-[16rem]' : '',
            isQuickAddToCartActivated &&
              'border-px border-solid border-clear transition-colors group-data-[hovered]/product-card:border-brand-2-900 group-focus-within/product-card:border-brand-2-900 mouse-only:group-hover/product-card:border-brand-2-900',
          )}
        >
          <div
            className={twMerge(
              'relative flex size-full grow gap-2',
              responsive ? 'flex-row desktop:flex-col' : 'flex-col',
            )}
          >
            {/* Desktop Card Tag */}
            {showTag && (
              <div
                className={twMerge(
                  'absolute left-0 top-0',
                  responsive ? 'hidden desktop:block' : 'block',
                )}
              >
                {customTag ? customTag : tag && <ProductTag tag={tag} />}
              </div>
            )}
            {/* Top Right Outlet Part */}
            {(isAddableToFavorites ??
              (isAuthenticated && product.type === ProductType.Article)) && (
              <div
                className={twMerge(
                  'absolute z-base',
                  responsive ? '-right-1 -top-1 desktop:right-0 desktop:top-0' : 'right-0 top-0',
                )}
                data-testid="product-card/right-button-section"
              >
                <AddProductToFavoriteButton
                  product={{
                    reference: mergedProduct.reference,
                    name: productName,
                    isFavorite,
                  }}
                />
              </div>
            )}
            {/* Image */}
            <div
              className={twMerge(
                'h-[8.75rem] flex-shrink-0',
                responsive
                  ? 'mt-0 h-auto self-auto desktop:mt-2 desktop:self-center'
                  : 'mt-2 self-center',
              )}
              data-testid="product-card/picture"
            >
              <Link
                variant="free"
                href={toProductPage(mergedProduct.urlKey)}
                onPress={trackProductPageAnalytics}
                intent={productRoutingIntent}
              >
                <CloudflarePictureProduct
                  className={responsive ? 'size-10 desktop:size-[8.75rem]' : 'size-[8.75rem]'}
                  src={mergedProduct.mainImage?.set?.[0]?.url}
                  alt={mergedProduct.mainImage?.alt || productName}
                  width={
                    responsive
                      ? { mobile: PRODUCT_IMAGE_SIZE_THUMBNAIL, desktop: PRODUCT_IMAGE_SIZE_MEDIUM }
                      : PRODUCT_IMAGE_SIZE_MEDIUM
                  }
                />
              </Link>
            </div>
            {/* Content */}
            <div
              className={twMerge(
                'flex grow flex-col gap-1',
                responsive ? 'min-w-22 desktop:w-auto' : '',
              )}
            >
              {/* Mobile Card Tag */}
              {showTag && (
                <div className={twMerge('w-fit', responsive ? 'block desktop:hidden' : 'hidden')}>
                  {customTag ? customTag : tag ? <ProductTag tag={tag} /> : null}
                </div>
              )}
              <Link
                variant="free"
                underlined="hover"
                className="inline-flex flex-col"
                href={toProductPage(mergedProduct.urlKey)}
                onPress={trackProductPageAnalytics}
                intent={productRoutingIntent}
              >
                {mergedProduct.brand && (
                  <span
                    className="mb-0.5 line-clamp-1 font-bold uppercase leading-light"
                    data-testid="product-card/brand"
                  >
                    {mergedProduct.brand.name}
                  </span>
                )}
                <span className={responsive ? 'h-auto desktop:h-11' : 'h-11'}>
                  <span
                    // TODO: Use the Tailwind v4 class once available.
                    className={twMerge(
                      'text-lg leading-light [overflow-wrap:anywhere]',
                      responsive ? 'line-clamp-2 desktop:line-clamp-3' : 'line-clamp-3',
                    )}
                    data-testid="product-card/product-name"
                  >
                    {productName}
                  </span>
                  {mergedProduct.reference && (
                    <span className="text-sm font-medium" data-testid="product-card/reference">
                      {t('productCard:ref', { ref: mergedProduct.reference })}
                    </span>
                  )}
                </span>
              </Link>
              <div className={responsive ? 'mt-auto desktop:mt-0 desktop:grow' : 'mt-auto'}>
                <div
                  className={twMerge(
                    'flex h-full flex-col justify-between',
                    responsive ? 'gap-1 desktop:gap-0.5' : 'gap-0.5',
                  )}
                >
                  <div
                    className={twMerge(
                      'flex flex-col justify-end',
                      showPricesAndEcoPart ? 'h-[3.1875rem]' : 'hidden',
                      responsive &&
                        `h-auto ${showPricesAndEcoPart ? 'tablet:h-[3.1875rem]' : 'tablet:hidden'}`, // product list has mobile height auto
                    )}
                  >
                    <Suspense fallback={priceSkeleton}>
                      {productPriceAndStock?.priceAndStockIsLoading ? (
                        priceSkeleton
                      ) : hasPrice ? (
                        <ProductPrice
                          size="xl"
                          price={displayPrice.price}
                          priceQuantity={displayPrice.priceQuantity}
                          discount={{
                            price: displayPrice.discountAmount,
                            percentage: displayPrice.discountPercentage,
                          }}
                          type={mergedProduct.type}
                          packaging={mergedProduct.stock?.packaging}
                          keepHeadSpace
                        />
                      ) : (
                        // Case 1, Case 2 and Case 3 — @see https://descourscabaudnet.sharepoint.com/:x:/r/sites/DigitalFactory/_layouts/15/doc.aspx?sourcedoc=%7B7b7c714a-58b2-4228-a022-953996989352%7D
                        availabilityIssue && (
                          <ProductAvailabilityIssueCard
                            issue={availabilityIssue}
                            favoritePOSPhoneNumber={favoritePOSPhoneNumber}
                          />
                        )
                      )}
                    </Suspense>
                  </div>
                  <div
                    className={twMerge(
                      'relative flex flex-col justify-end gap-1',
                      'h-6 overflow-hidden',
                      responsive && `h-auto tablet:h-6`, // product list has mobile height auto
                      badgesWrapperClassname,
                    )}
                  >
                    <div
                      className={twMerge(
                        'w-full',
                        showAddToCart &&
                          'transition-[opacity,visibility] group-data-[hovered]/product-card:invisible group-data-[hovered]/product-card:opacity-0 group-focus-within/product-card:invisible group-focus-within/product-card:opacity-0 mouse-only:group-hover/product-card:invisible mouse-only:group-hover/product-card:opacity-0',
                      )}
                    >
                      {productPriceAndStock?.priceAndStockIsLoading
                        ? null
                        : /* Case 4 to 19 */
                          availabilityIssue === null &&
                          mergedProduct.type === ProductType.Article && (
                            <ProductCardBadges
                              product={mergedProduct}
                              isClickAndCollectEnabled={isClickAndCollectEnabled}
                            />
                          )}
                    </div>
                    <Suspense fallback={subArticlesSkeleton}>
                      {mergedProduct.type === ProductType.Product &&
                        (productPriceAndStock?.priceAndStockIsLoading ? (
                          subArticlesSkeleton
                        ) : mergedProduct.nbArticles ? (
                          <Link
                            variant="free"
                            icon={<Icon.Blend size="sm" />}
                            className="whitespace-nowrap leading-light"
                            href={toProductPage(mergedProduct.urlKey)}
                            onPress={trackProductPageAnalytics}
                            intent={productRoutingIntent}
                            underlined="hover"
                          >
                            {t('productCard:subArticles', { count: mergedProduct.nbArticles })}
                          </Link>
                        ) : null)}
                    </Suspense>
                    {!isAuthenticated && (
                      <ProductPriceLoginLink
                        origin="product-card"
                        isPublicPriceEnabled={isPublicPriceEnabled}
                        hasNationalDiscount={!!mergedProduct.price?.discountExclTax}
                      />
                    )}
                    {showAddToCart && (
                      <ProductCardAddToCartButton product={mergedProduct} source={source} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {bottomNode}
        </Card>
      </ProductCardHoverGroup>
    </div>
  )
})
